import { useState, useMemo, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchFieldMappings, deleteMapping, createFieldMapping } from "@/lib";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { getSyncModeClass, getFieldTypeClass } from "@shared/utils";
import { FieldMapping, SyncMode } from "@shared/api-types";
import { useToast } from "@/hooks/use-toast";
import FieldMappingForm from "./field-mapping-form";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Edit,
  Plus,
  Trash2,
  Search,
  X,
  List,
  Layers,
} from "lucide-react";
import { usePermissions } from "@/contexts/PermissionContext";

// Types for sorting
type SortField = 'entityName' | 'kupField' | 'hsField' | 'syncMode' | 'isChoiceField';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'flat' | 'grouped';

interface FieldMappingsTableProps {
  onAddMapping: () => void;
}

export default function FieldMappingsTable({ onAddMapping }: FieldMappingsTableProps) {
  // Filter state
  const [entityFilter, setEntityFilter] = useState<string>("all_entities");
  const [syncModeFilter, setSyncModeFilter] = useState<string>("all_sync_modes");
  const [searchQuery, setSearchQuery] = useState("");

  // Sort state
  const [sortField, setSortField] = useState<SortField>('entityName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('flat');
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal state
  const [mappingToDelete, setMappingToDelete] = useState<FieldMapping | null>(null);
  const [mappingToEdit, setMappingToEdit] = useState<FieldMapping | null>(null);

  // Inline editing state
  const [editingCell, setEditingCell] = useState<{
    rowKey: string;
    field: 'syncMode' | 'isChoiceField';
  } | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('fieldMappings', 'edit');
  const noEditMessage = "You don't have permission to edit Field Mappings";

  const ensureCanEdit = (): boolean => {
    if (!canEdit) {
      toast({
        title: "Access denied",
        description: noEditMessage,
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  // Fetch data
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["/api/field-mappings", "field"],
    queryFn: () => fetchFieldMappings(),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ({
      partitionKey,
      rowKey,
    }: {
      partitionKey: string;
      rowKey: string;
    }) => deleteMapping(partitionKey, rowKey),
    onSuccess: () => {
      toast({
        title: "Mapping deleted",
        description: "The field mapping has been deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/statistics"] });
      setMappingToDelete(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete the field mapping.",
        variant: "destructive",
      });
    },
  });

  // Inline update mutation
  const inlineUpdateMutation = useMutation({
    mutationFn: (mapping: FieldMapping) => createFieldMapping({
      type: 'field',
      entityName: mapping.entityName,
      kupField: mapping.kupField,
      hsField: mapping.hsField,
      isChoiceField: mapping.isChoiceField,
      syncMode: mapping.syncMode,
    }),
    onSuccess: () => {
      toast({ title: "Updated", description: "Mapping updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/all"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/statistics"] });
      setEditingCell(null);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update mapping", variant: "destructive" });
    },
  });

  // Filter data
  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((mapping) => {
      // Apply entity filter
      if (entityFilter !== "all_entities" && mapping.entityName !== entityFilter) {
        return false;
      }

      // Apply sync mode filter
      if (syncModeFilter !== "all_sync_modes") {
        const normalizedSyncMode = mapping.syncMode?.toLowerCase();
        const normalizedFilter = syncModeFilter.toLowerCase();
        if (normalizedSyncMode !== normalizedFilter) {
          return false;
        }
      }

      // Apply search
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return (
          mapping.entityName.toLowerCase().includes(query) ||
          mapping.kupField.toLowerCase().includes(query) ||
          mapping.hsField.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [data, entityFilter, syncModeFilter, searchQuery]);

  // Sort data
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aVal: string | boolean = a[sortField];
      let bVal: string | boolean = b[sortField];

      // Handle boolean for isChoiceField
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 'Choice' : 'Text';
        bVal = bVal as boolean ? 'Choice' : 'Text';
      }

      const comparison = String(aVal || '').localeCompare(String(bVal || ''));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortField, sortDirection]);

  // Group data by entity
  const groupedData = useMemo(() => {
    return sortedData.reduce((acc, mapping) => {
      const entity = mapping.entityName;
      if (!acc[entity]) acc[entity] = [];
      acc[entity].push(mapping);
      return acc;
    }, {} as Record<string, FieldMapping[]>);
  }, [sortedData]);

  // Get unique entity names for filter
  const uniqueEntityNames = useMemo(() => {
    return Array.from(new Set(data?.map((m) => m.entityName) || [])).sort();
  }, [data]);

  // Pagination
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (type: 'entity' | 'syncMode', value: string) => {
    if (type === 'entity') {
      setEntityFilter(value);
    } else {
      setSyncModeFilter(value);
    }
    setCurrentPage(1);
  };

  const toggleEntity = (entity: string) => {
    setExpandedEntities(prev => {
      const next = new Set(prev);
      if (next.has(entity)) next.delete(entity);
      else next.add(entity);
      return next;
    });
  };

  const expandAll = () => setExpandedEntities(new Set(Object.keys(groupedData)));
  const collapseAll = () => setExpandedEntities(new Set());

  const handleDeleteClick = (mapping: FieldMapping) => {
    if (!ensureCanEdit()) return;
    setMappingToDelete(mapping);
  };

  const handleConfirmDelete = () => {
    if (!ensureCanEdit()) return;
    if (mappingToDelete) {
      deleteMutation.mutate({
        partitionKey: "FieldMapping",
        rowKey: `${mappingToDelete.entityName}_${mappingToDelete.kupField}`,
      });
    }
  };

  const handleEditClick = (mapping: FieldMapping) => {
    if (!ensureCanEdit()) return;
    setMappingToEdit(mapping);
  };

  const closeDeleteDialog = () => setMappingToDelete(null);
  const closeEditModal = () => setMappingToEdit(null);

  const syncModes = [
    { value: "all_sync_modes", label: "All Sync Modes" },
    { value: "IfEmpty", label: "If Empty" },
    { value: "Always", label: "Always" },
    { value: "Ignore", label: "Ignore" },
    { value: "Custom", label: "Custom" },
  ];

  const getSyncModeLabel = (mode: string) => {
    switch (mode?.toLowerCase()) {
      case 'ifempty': return 'If Empty';
      case 'always': return 'Always';
      case 'ignore': return 'Ignore';
      case 'custom': return 'Custom';
      default: return mode || 'Unknown';
    }
  };

  // Sortable header component
  const SortableHeader = ({ field, label }: { field: SortField; label: string }) => (
    <TableHead
      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700 select-none dark:text-zinc-100"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortField === field ? (
          sortDirection === 'asc'
            ? <ChevronUp className="h-4 w-4" />
            : <ChevronDown className="h-4 w-4" />
        ) : (
          <span className="w-4" />
        )}
      </div>
    </TableHead>
  );

  // Render row content (used in both flat and grouped views)
  const renderRowContent = (mapping: FieldMapping, showEntity: boolean = true) => {
    const rowKey = `${mapping.entityName}_${mapping.kupField}`;
    const isEditingSyncMode = editingCell?.rowKey === rowKey && editingCell?.field === 'syncMode';

    return (
      <>
        <TableCell className={`dark:text-zinc-100 ${!showEntity ? 'pl-8' : ''}`}>
          {showEntity ? mapping.entityName : '—'}
        </TableCell>
        <TableCell className="dark:text-zinc-100">{mapping.kupField}</TableCell>
        <TableCell className="dark:text-zinc-100">{mapping.hsField}</TableCell>
        <TableCell>
          <Badge
            variant="outline"
            title={!canEdit ? noEditMessage : undefined}
            className={`${getFieldTypeClass(mapping.isChoiceField ? "choice" : "text")} ${canEdit ? 'cursor-pointer hover:ring-2 hover:ring-offset-1' : 'opacity-50 cursor-not-allowed'} dark:border-zinc-600 dark:text-zinc-200`}
            onClick={() => {
              if (!ensureCanEdit()) return;
              inlineUpdateMutation.mutate({
                ...mapping,
                isChoiceField: !mapping.isChoiceField
              });
            }}
          >
            {mapping.isChoiceField ? "Choice" : "Text"}
          </Badge>
        </TableCell>
        <TableCell>
          {isEditingSyncMode ? (
            <Select
              value={mapping.syncMode}
              onValueChange={(value) => {
                inlineUpdateMutation.mutate({ ...mapping, syncMode: value as SyncMode });
              }}
              onOpenChange={(open) => { if (!open) setEditingCell(null); }}
              defaultOpen
            >
              <SelectTrigger className="w-[120px] dark:bg-zinc-800 dark:border-zinc-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark:bg-zinc-800">
                {syncModes.filter(m => m.value !== 'all_sync_modes').map(mode => (
                  <SelectItem key={mode.value} value={mode.value} className="dark:text-zinc-100">
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge
              variant="outline"
              title={!canEdit ? noEditMessage : undefined}
              className={`${getSyncModeClass(mapping.syncMode)} ${canEdit ? 'cursor-pointer hover:ring-2 hover:ring-offset-1' : 'opacity-50 cursor-not-allowed'} dark:border-zinc-600 dark:text-zinc-200`}
              onClick={() => {
                if (!ensureCanEdit()) return;
                setEditingCell({ rowKey, field: 'syncMode' });
              }}
            >
              {getSyncModeLabel(mapping.syncMode)}
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-right space-x-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEditClick(mapping)}
            disabled={!canEdit}
            title={!canEdit ? noEditMessage : undefined}
            className={`dark:text-zinc-300 dark:hover:text-white ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDeleteClick(mapping)}
            disabled={!canEdit}
            title={!canEdit ? noEditMessage : undefined}
            className={`text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TableCell>
      </>
    );
  };

  if (isError) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow p-2 mb-6">
        <h3 className="text-red-500 dark:text-red-400">Error loading field mappings: {String(error)}</h3>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        {/* Header with title and controls */}
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <h2 className="text-xl font-semibold dark:text-zinc-100">Field Mappings</h2>
            <Button
              onClick={() => {
                if (!ensureCanEdit()) return;
                onAddMapping();
              }}
              disabled={!canEdit}
              title={!canEdit ? noEditMessage : undefined}
              className={`flex gap-1 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Plus size={16} />
              Add Mapping
            </Button>
          </div>

          {/* Search and filters row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search mappings..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 w-[200px] dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => handleSearchChange("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Entity filter */}
            <Select value={entityFilter} onValueChange={(v) => handleFilterChange('entity', v)}>
              <SelectTrigger className="w-[180px] dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100">
                <SelectValue placeholder="Filter by entity" />
              </SelectTrigger>
              <SelectContent className="dark:bg-zinc-800 dark:text-zinc-100">
                <SelectItem value="all_entities">All Entities</SelectItem>
                {uniqueEntityNames.map((entity) => (
                  <SelectItem key={entity} value={entity} className="dark:text-zinc-100">
                    {entity}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sync mode filter */}
            <Select value={syncModeFilter} onValueChange={(v) => handleFilterChange('syncMode', v)}>
              <SelectTrigger className="w-[160px] dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100">
                <SelectValue placeholder="Filter by sync mode" />
              </SelectTrigger>
              <SelectContent className="dark:bg-zinc-800 dark:text-zinc-100">
                {syncModes.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value} className="dark:text-zinc-100">
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* View mode toggle */}
            <div className="flex items-center gap-1 ml-auto">
              <Button
                variant={viewMode === 'flat' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('flat')}
                className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
              >
                <List className="h-4 w-4 mr-1" /> Flat
              </Button>
              <Button
                variant={viewMode === 'grouped' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grouped')}
                className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
              >
                <Layers className="h-4 w-4 mr-1" /> Grouped
              </Button>
              {viewMode === 'grouped' && (
                <>
                  <Button variant="ghost" size="sm" onClick={expandAll} className="dark:text-zinc-300">
                    Expand All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={collapseAll} className="dark:text-zinc-300">
                    Collapse All
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-500 dark:text-zinc-400">
            {filteredData.length} result{filteredData.length !== 1 ? 's' : ''}
            {searchQuery && ` for "${searchQuery}"`}
            {entityFilter !== 'all_entities' && ` in ${entityFilter}`}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border dark:border-zinc-700">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 dark:bg-zinc-800">
                <SortableHeader field="entityName" label="Entity" />
                <SortableHeader field="kupField" label="KUP Field" />
                <SortableHeader field="hsField" label="HS Field" />
                <SortableHeader field="isChoiceField" label="Type" />
                <SortableHeader field="syncMode" label="Sync Mode" />
                <TableHead className="text-right dark:text-zinc-100">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array(5).fill(0).map((_, i) => (
                  <TableRow key={i} className="dark:bg-zinc-900">
                    {Array(6).fill(0).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-20 dark:bg-zinc-700" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : viewMode === 'grouped' ? (
                // Grouped view
                Object.entries(groupedData).length === 0 ? (
                  <TableRow className="dark:bg-zinc-900">
                    <TableCell colSpan={6} className="text-center py-4 dark:text-zinc-300">
                      No field mappings found
                    </TableCell>
                  </TableRow>
                ) : (
                  Object.entries(groupedData).map(([entity, mappings]) => (
                    <Fragment key={entity}>
                      <TableRow
                        className="bg-gray-100 dark:bg-zinc-800 cursor-pointer hover:bg-gray-200 dark:hover:bg-zinc-700"
                        onClick={() => toggleEntity(entity)}
                      >
                        <TableCell colSpan={6} className="font-semibold dark:text-zinc-100">
                          <div className="flex items-center gap-2">
                            {expandedEntities.has(entity)
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />
                            }
                            {entity}
                            <Badge variant="secondary" className="dark:bg-zinc-600 dark:text-zinc-200">
                              {mappings.length}
                            </Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedEntities.has(entity) && mappings.map(mapping => (
                        <TableRow
                          key={`${mapping.entityName}_${mapping.kupField}`}
                          className="dark:bg-zinc-900"
                        >
                          {renderRowContent(mapping, false)}
                        </TableRow>
                      ))}
                    </Fragment>
                  ))
                )
              ) : (
                // Flat view with pagination
                paginatedData.length === 0 ? (
                  <TableRow className="dark:bg-zinc-900">
                    <TableCell colSpan={6} className="text-center py-4 dark:text-zinc-300">
                      No field mappings found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((mapping) => (
                    <TableRow
                      key={`${mapping.entityName}_${mapping.kupField}`}
                      className="dark:bg-zinc-900"
                    >
                      {renderRowContent(mapping, true)}
                    </TableRow>
                  ))
                )
              )}
            </TableBody>
          </Table>

          {/* Pagination (only in flat view) */}
          {viewMode === 'flat' && sortedData.length > itemsPerPage && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-zinc-800">
              <div className="text-sm text-gray-700 dark:text-zinc-200">
                Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
                <span className="font-medium">{Math.min(endIndex, totalItems)}</span>{" "}
                of <span className="font-medium">{totalItems}</span> results
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage <= 1}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600"
                >
                  <ChevronRight className="h-4 w-4" />
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Delete confirmation dialog */}
        {mappingToDelete && (
          <AlertDialog open={!!mappingToDelete} onOpenChange={closeDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete the mapping for{" "}
                  <strong>
                    {mappingToDelete.entityName}.{mappingToDelete.kupField}
                  </strong>
                  ? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirmDelete}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Edit form modal */}
        {mappingToEdit && (
          <FieldMappingForm
            mapping={mappingToEdit}
            isOpen={!!mappingToEdit}
            onClose={closeEditModal}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
              queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/all"] });
              queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/statistics"] });
              closeEditModal();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
